<template>
  <div class="geo-map" :class="{ 'is-hovered': isHovered, 'is-loading': isLoading }">
    <svg :width="width + margin.left + margin.right" :height="height + margin.top + margin.bottom">
      <g ref="world" v-if="countries">
        <path v-for="(item, index) in countries.features" :key="index" :d="path(item)" :fill="getFill(item)" :stroke="getStroke(item)" @mouseenter="mouseenter(item)" @mouseleave="mouseleave(item)" />
      </g>
      <g ref="legend" class="legend" :style="{ 'transform': `translate(${width - legendWidth}px, 7px)` }">
        <g v-for="(item, index) in reverseColors" :key="index">
          <rect :fill="data.length > 0 ? item : '#f2f2f2'" :width="legendWidth" :height="legendHeight" :style="{ 'transform': `translate(0px, ${index * legendHeight}px)` }" />
          <rect :fill="data.length > 0 ? item : '#f2f2f2'" :width="legendWidth" :height="legendHeight" :style="{ 'transform': `translate(0px, ${index * legendHeight}px)` }" />
          <text v-if="data.length > 0" :style="{ 'transform': `translate(-12px, ${index * legendHeight + 4}px)` }">{{ color.invertExtent(item)[1] || $tc('charts.geo_map.tooltip_users_amount', color.domain()[0] * color.range().length, { amount: color.domain()[0] * color.range().length }) }}</text>
        </g>
        <text v-if="data.length > 0" :style="{ 'transform': `translate(-12px, ${color.range().length * legendHeight + 4}px)` }">0</text>
      </g>
    </svg>
    <div class="tooltip" role="tooltip" ref="tooltip">
      <div class="tooltip-inner">
        <div class="title is-spaced">{{ tooltipTitle }} &middot; {{ tooltipValue }}%</div>
        <div class="subtitle">{{ $tc('charts.geo_map.tooltip_users_amount', tooltipSubtitle, { amount: tooltipSubtitle }) }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { json } from 'd3-request';
import { select, event, clientPoint } from 'd3-selection';
import { geoPath, geoEquirectangular } from 'd3-geo';
import { scaleThreshold } from 'd3-scale';
import * as topojson from 'topojson-client';
import { filter, assignIn, find, reverse, map, maxBy } from 'lodash';
import { resizable } from 'views/mixins'

export default {

  mixins: [resizable],

  props: {
    data: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      margin: { top: 0, right: 0, bottom: 0, left: 0 },
      width: 0,
      height: 0,
      projection: null,
      path: null,
      colors: [],
      color: null,
      countries: {},
      isHovered: false,
      legendWidth: 5,
      legendHeight: 0,
      tooltipPos: [0, 0],
      tooltipTitle: '',
      tooltipValue: '',
      tooltipSubtitle: ''
    };
  },

  computed: {
    reverseColors() {
      return reverse(this.colors);
    },

    isLoading() {
      return this.data.length === 0;
    }
  },

  mounted() {
    this.colors = ['#aadcf4', '#4f91c8', '#29478d', '#000249'];
    this.color = scaleThreshold()
      .range(this.colors);

    this.projection = geoEquirectangular()
      .scale(1)
      .translate([0, 0]);

    this.path = geoPath()
      .projection(this.projection);

    this.getWorld().then(
      world => {
        let countries = world.objects.countries;

        countries = assignIn({}, countries, {
          type: countries.type,
          geometries: filter(countries.geometries, d => d.id !== '010')
        });

        countries = topojson.feature(world, countries);

        this.countries = this.addData(countries, this.data);

        const domain = this.getDomain(this.data, this.colors.length);

        this.color.domain(domain);

        this.width = this.$el.getBoundingClientRect().width - this.margin.left - this.margin.right;
        this.height = this.$el.getBoundingClientRect().height - this.margin.top - this.margin.bottom;

        this.legendHeight = (this.height - 20) / this.colors.length;

        this.projection.fitSize([this.width - 80, this.height], this.countries);
      },
      err => console.log(err)
    );

    select(this.$refs.world)
      .on('mousemove', this.mousemove);
  },

  methods: {
    onResize() {
      this.width = 0;

      this.$nextTick(() => {
        this.width = this.$el.getBoundingClientRect().width - this.margin.left - this.margin.right;
        this.projection.fitSize([this.width - 80, this.height], this.countries);
      });
    },

    getWorld() {
      return new Promise((resolve, reject) => {
        json('static/world/110m.json', (error, world) => {
          if (error) {
            reject(error);
          }

          resolve(world);
        });
      });
    },

    addData(countries, data) {
      if (data.length === 0) {
        return countries;
      }

      const features = map(countries.features, feature => {
        const country = find(data, o => {
          return feature.id === o[0];
        });

        if (country) {
          return assignIn({}, feature, {
            data: country
          });
        }

        return feature;
      });

      return assignIn({}, countries, {
        features: features
      });
    },

    getFill(country) {
      if (!country.data) {
        return 'none';
      }

      const value = country.data[2];

      return this.color(value);
    },

    getStroke(country) {
      return country.data ? '#fff' : '#f2f2f2';
    },

    getDomain(data, count) {
      const max = this.data.length > 0 ? maxBy(this.data, o => o[2])[2] : 0;
      const nice = this.nice(max);
      const tick = nice / count;

      const domain = map(new Array(count - 1), (o, i) => {
        return tick * i + tick;
      });

      return domain;
    },

    nice(value) {
      const l = value.toString().length;
      const log = Math.pow(10, l - 1);
      const nice = Math.ceil(value / log) * log;
      
      return nice;
    },

    mousemove() {
      const pos = clientPoint(this.$refs.world, event);

      select(this.$refs.tooltip)
        .style('transform', `translate(${pos[0]}px, ${pos[1]}px)`);
    },

    mouseenter(country) {
      if (!country.data) {
        return;
      }

      const data = country.data;

      this.tooltipValue = data[3];
      this.tooltipTitle = data[1];
      this.tooltipSubtitle = data[2];
      this.isHovered = true;
    },

    mouseleave(country) {
      if (!country.data) {
        return;
      }
      
      this.isHovered = false;
    }
  },
  watch: {
    data: function (newData) {
      this.data = newData;
      this.countries = this.addData(this.countries, this.data);

      const domain = this.getDomain(this.data, this.colors.length);

      this.color.domain(domain);
    }
  }
};
</script>

<style lang="scss">
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.geo-map {
  position: relative;

  &.is-loading {
    path {
      fill: #f2f2f2;
      stroke: #fff;
    }
  }

  path {
    stroke-width: .5px;
    transition: $transition;
  }

  &.is-hovered {
    .tooltip {
      opacity: 1;
    }

    path {
      opacity: .5;

      &:hover {
        opacity: 1;
      }
    }
  }

  .legend {
    text {
      font-family: $family-sans-serif;
      font-size: $size-small;
      line-height: 1.5;
      opacity: .5;
      fill: $grey-darker;
      text-anchor: end;
    }
  }

  .tooltip {
    margin-left: 1rem;
    top: 1.25rem;
    left: 0;
    transition: none;

    .tooltip-inner {
      padding: .75rem;
    }

    .title {
      font-size: $size-small;
      font-weight: $weight-medium;
      color: $grey-darker;
      margin-bottom: .25rem;
    }

    .subtitle {
      font-size: .6875rem;
      color: $grey-darker;
      margin-bottom: 0;
    }
  }
}
</style>
